import { createBrowserRouter } from 'react-router-dom'

import RouteError from '@/routes/RouteError'
import RouteFallback from '@/routes/RouteFallback'
import { navigateTo } from '@/routes/navigateTo'
import { routes } from '@/routes/routes'
// TODO(api): enable role based access once the session user carries
// `role` and `access_control` (see routes/routeAccess.js).
// import { filterRoutesByAccess } from '@/routes/routeAccess'

/** Wraps a dynamic import so guards and layouts stay out of the entry chunk. */
const lazyComponent = (loader) => async () => ({ Component: (await loader()).default })

/**
 * Each `layout` in the route configuration resolves to a guard and, optionally,
 * a shell rendered around that branch's children.
 */
const LAYOUT_WRAPPERS = {
  dashboard: {
    guard: () => import('@/routes/ProtectedRoute'),
    layout: () => import('@/layouts/DashboardLayout'),
  },
  auth: {
    guard: () => import('@/routes/GuestRoute'),
    layout: null,
  },
}

/**
 * Maps one configured route onto a React Router object. Children recurse, so
 * nested routes need no special handling.
 */
const toRouteObject = (route) => {
  const object = route.index ? { index: true } : { path: route.path }

  if (route.redirectTo) object.Component = navigateTo(route.redirectTo)
  else if (route.element) object.lazy = route.element

  if (route.children) object.children = route.children.map(toRouteObject)

  // Metadata read by the sidebar, breadcrumbs and the document title.
  object.handle = {
    id: route.id,
    title: route.name,
    crumb: route.name && !route.hideCrumb ? () => route.name : undefined,
    roles: route.roles,
  }

  return object
}

const toBranch = (branch) => {
  const { guard, layout } = LAYOUT_WRAPPERS[branch.layout]
  const children = branch.children.map(toRouteObject)

  return {
    lazy: lazyComponent(guard),
    ErrorBoundary: RouteError,
    HydrateFallback: RouteFallback,
    children: layout ? [{ lazy: lazyComponent(layout), children }] : children,
  }
}

/**
 * `user` is threaded in so access filtering can be switched on without
 * restructuring the router.
 */
export const buildRouter = (/* user */) => {
  // TODO(api): const visibleRoutes = filterRoutesByAccess(routes, user)
  const visibleRoutes = routes

  return createBrowserRouter(visibleRoutes.map(toBranch))
}

export const router = buildRouter()
